import { Component, Vue, Prop } from 'vue-property-decorator';
import { Btn, FullScreenToggle, FormButtonDisabler, Tabs, Tab, ISideDrawerContent } from '@samc/centricity-core';
import { LsGrid } from '@/Components/LSGrid';
import { IReviewOrderDetails, IReviewOrderPackageDetail } from '@/Interfaces';
import { pluralize, toString, AjaxHelper, errToMessage } from '@samc/common';
import { download } from '@/utils';
import Entitlements from '@/Entitlements';
import { IOrderItem } from '../../../Pages/Services/ReviewOrders/Interfaces';
import { format } from 'date-fns';

const receivedOrderStatus = 'Received';

@Component({
  name: 'review-order-view-side-drawer',
  components: { Btn, FullScreenToggle, LsGrid, FormButtonDisabler, Tabs, Tab }
})
export default class ReviewOrderViewSideDrawer extends Vue implements ISideDrawerContent {
  @Prop() public addCloseProp!: (callback: (preventDefault: () => void) => any) => void;
  @Prop() public cancelProp!: () => void;
  @Prop() public actionProp!: () => void;

  @Prop({ required: true }) public orderTitle!: string;
  @Prop({ required: true }) public reviewOrderId!: number;
  @Prop({ default: () => null }) public packageId!: string | null;
  @Prop({ default: 'global' }) public type!: 'global' | 'sell' | 'acquisition';
  @Prop({ default: false }) public showReceived!: boolean;
  @Prop({ type: Boolean, default: false }) public hideEdit!: boolean;

  public reviewOrder: IReviewOrderDetails | null = null;

  public isDisabled: boolean = false;
  public tabIndex: number = 0;

  public get hasEditPermissions(): boolean { return this.$user !== null && this.$user.hasEntitlement(Entitlements.ReviewOrderModule.UpdateReviewOrder); }
  public get hasExportPermission(): boolean { return this.$user !== null && this.$user.hasEntitlement(Entitlements.ReviewOrderModule.ExportReviewOrder); }

  public get packageName(): string { return this.getPBSName(this.reviewOrder?.packages ?? [], x => x.packageName ?? ''); }
  public get buyerName(): string { return this.getPBSName(this.reviewOrder?.packages ?? [], x => x.buyerName ?? ''); }
  public get sellerName(): string { return this.getPBSName(this.reviewOrder?.packages ?? [], x => x.sellerName ?? ''); }

  public get orderItems(): IOrderItem[] { return this.reviewOrder?.orderItems ?? []; }
  public get receivedOrderItems(): IOrderItem[] { return this.orderItems.filter(x => x.currentStatus === receivedOrderStatus); }
  public get orderCount(): number { return this.reviewOrder?.orderItems.length ?? 0; }

  public get hasReceived(): boolean { return this.receivedOrderItems.length > 0; }

  public get formatString() { return toString; }
  public get pluralize() { return pluralize; }

  public async mounted() {
    try {
      if (this.showReceived)
        this.tabIndex = 1; // Set the tab index to be the recieved item

      this.$loading('Loading Review Details');

      const helper = new AjaxHelper('', null, null, null);
      this.reviewOrder = await helper.getData<IReviewOrderDetails>(`/componentapi/revieworder/${this.reviewOrderId}/details`);
    } catch (err: any) {
      this.$log('Loading Review Order Details Failed', err);
      this.$alert({
        type: 'error',
        title: 'Loading Review Order Details Failed',
        errors: errToMessage(err),
        closingOption: 'holdOpen'
      });
    } finally {
      this.$loading();
    }
  }

  public editReviewOrder() {
    if (this.reviewOrder !== null) {
      if (this.packageId !== null) {
        window.location.href = `/Services/ReviewOrders/${this.packageId}?reviewOrderId=${this.reviewOrderId}&type=${this.type}`;
      } else {
        window.location.href = `/Services/ReviewOrders?reviewOrderId=${this.reviewOrderId}&type=${this.type}`;
      }
    }
  }

  public async exportReviewOrder() {
    if (this.reviewOrder !== null) {
      try {
        this.$loading('Generating Export');
        const helper = new AjaxHelper('', null, null, null);
        const data = await helper.getData<Blob>(`/componentapi/revieworder/${this.reviewOrderId}/export`, { packageId: this.packageId }, { responseType: 'blob' });

        const fileName = `${this.reviewOrder.reviewTypeName}_${this.packageName}_${format(new Date(this.reviewOrder.createdDate), 'MMddyyyy_hhmma')}`;
        download(data, `${fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      } catch (err: any) {
        this.$log('Something went wrong during export', err);
        this.$alert.error('Export failed to download');
      } finally {
        this.$loading();
      }
    }
  }

  /**
   * Helper method meant to get the name of the package based on the callback or if there are multiple packages this review
   * order is attached to
   * 
   * @param details The package details we are working with
   * @param nameCallback The name callback to get the current name we are working on
   */
  private getPBSName(details: IReviewOrderPackageDetail[], nameCallback: (item: IReviewOrderPackageDetail) => string) {
    const packageDetails = details.filter(x => {
      switch (this.type) {
        case 'acquisition': return x.sellerId !== null;
        case 'sell': return x.sellerId === null;
        default: return true;
      }
    });
    if (packageDetails.length === 0) return '';
    if (packageDetails.length > 1) return 'Multiple';
    return nameCallback(packageDetails[0]);
  }
}